import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { environment } from '../../../environments/environment';
import { nestSelectionLists } from '../../../next/CustomDash/filters/selectionList/nestSelectionLists';
import type { TreeSelection } from '../../../next/CustomDash/filters/types/filters';
import { useRoleSelectionList } from '../../selection-lists/hooks/useRoleSelectionList';
import { useSelectionLists } from '../../selection-lists/hooks/useSelectionLists';
import type { AllSelectionListsWithSkillTaxonomyKey } from '../../selection-lists/types';
import type { CompanyMapperSearchResult } from '../../types/company-result-item';

export const MIN_SEARCH_LENGTH = 2;

export type EntitySearchOption = {
  label: string;
  value: string;
  data: TreeSelection;
};

export type EntitySearchSelectConfig = {
  options: EntitySearchOption[];
  isLoading: boolean;
  onInputChange?: (value: string) => void;
  searchTerm?: string;
  searchResultsCount?: number;
};

type NestedNode = {
  id: string;
  name: string;
  children?: NestedNode[];
  depth: number;
};

const useCompanyTypeahead = (searchTerm: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['company-search', searchTerm],
    queryFn: async ({ signal }) => {
      const response = await fetch(`${environment.COMPANY_MAPPER_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: searchTerm, employee_cnt_min: 1 }),
        signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json() as Promise<CompanyMapperSearchResult[]>;
    },
    enabled: searchTerm.trim().length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
  });

  const rawMappedResults = useMemo(
    () =>
      (data ?? [])
        .sort((a, b) => (b.raw_emp_count || 0) - (a.raw_emp_count || 0))
        .map((result) => ({
          id: `${result.primary_identifiers?.rics_k10}_${result.primary_identifiers?.rics_k400}_${result.company_id}`,
          name: result.primary_identifiers?.primary_name ?? result.primary_name,
        })),
    [data]
  );

  return {
    rawMappedResults,
    isLoading: isLoading && searchTerm.trim().length >= MIN_SEARCH_LENGTH,
  };
};

export const treeSelectionToEntitySearchOption = (
  selection: TreeSelection
): EntitySearchOption => ({
  label: selection.name,
  value: selection.id,
  data: selection,
});

const ensureSelectionsPresent = (
  options: EntitySearchOption[],
  selections: TreeSelection[]
): EntitySearchOption[] => {
  const optionMap = new Map(options.map((option) => [option.value, option]));
  selections.forEach((selection) => {
    if (!optionMap.has(selection.id)) {
      optionMap.set(selection.id, treeSelectionToEntitySearchOption(selection));
    }
  });
  return Array.from(optionMap.values());
};

export const useCompanyEntitySearchConfig = (
  storedSelections: TreeSelection[]
): EntitySearchSelectConfig => {
  const [searchTerm, setSearchTerm] = useState('');
  const initialData = storedSelections;
  const baseOptions = initialData.map(treeSelectionToEntitySearchOption);

  const ricsLists = useSelectionLists(['rics_k10', 'rics_k400']);
  const { rawMappedResults, isLoading } = useCompanyTypeahead(searchTerm);

  // Build nested industry tree and flatten to get K10 (depth 0) and K400 (depth 1) options
  const industryOptions = useMemo(() => {
    if (!ricsLists?.rics_k10 || !ricsLists?.rics_k400) return [];

    const nested = nestSelectionLists([
      {
        selectionListId: 'rics_k10',
        selectionList: ricsLists.rics_k10 ?? [],
      },
      {
        selectionListId: 'rics_k400',
        selectionList: ricsLists.rics_k400 ?? [],
      },
    ]) as NestedNode[];

    const collectAllNodes = (nodes: NestedNode[]): NestedNode[] =>
      nodes.flatMap((n) => [
        n,
        ...(n.children ? collectAllNodes(n.children) : []),
      ]);

    const depthToLevel: Record<number, AllSelectionListsWithSkillTaxonomyKey> =
      {
        0: 'rics_k10',
        1: 'rics_k400',
      };

    return collectAllNodes(nested).map((node) =>
      treeSelectionToEntitySearchOption({
        id: node.id,
        name: node.name,
        levelId: (depthToLevel[node.depth] ??
          'rics_k10') as AllSelectionListsWithSkillTaxonomyKey,
      })
    );
  }, [ricsLists]);

  const searchResults = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    // Company results from typeahead
    const companyOptions = rawMappedResults.map((item) =>
      treeSelectionToEntitySearchOption({
        id: item.id,
        name: item.name,
        levelId: 'company',
      })
    );

    // Filter industry options by search term
    const filteredIndustryOptions = industryOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearchTerm)
    );

    // Merge company and industry results
    return [...companyOptions, ...filteredIndustryOptions];
  }, [rawMappedResults, searchTerm, industryOptions]);

  const options = useMemo(() => {
    const pool =
      searchTerm.trim().length >= MIN_SEARCH_LENGTH
        ? searchResults
        : baseOptions;
    return ensureSelectionsPresent(pool, storedSelections);
  }, [searchResults, baseOptions, searchTerm, storedSelections]);

  const handleInputChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  return {
    options,
    isLoading,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: searchResults.length,
  };
};

type GeographyConfigOptions = {
  showSuggestionsWhenEmpty?: boolean;
};

export const useGeographyEntitySearchConfig = (
  storedSelections: TreeSelection[],
  { showSuggestionsWhenEmpty = true }: GeographyConfigOptions = {}
): EntitySearchSelectConfig => {
  const geographyLists = useSelectionLists(['region', 'country', 'msa']);
  // Always load city list (it's cached, so no performance issue)
  // We'll only use it when searching (>=2 chars)
  const cityLists = useSelectionLists(['city']);
  const [searchTerm, setSearchTerm] = useState('');

  const allOptions = useMemo(() => {
    if (!geographyLists) return [] as EntitySearchOption[];

    const nested = nestSelectionLists([
      { selectionListId: 'region', selectionList: geographyLists.region ?? [] },
      {
        selectionListId: 'country',
        selectionList: geographyLists.country ?? [],
      },
      { selectionListId: 'msa', selectionList: geographyLists.msa ?? [] },
    ]) as NestedNode[];

    const collectAllNodes = (nodes: NestedNode[]): NestedNode[] =>
      nodes.flatMap((n) => [
        n,
        ...(n.children ? collectAllNodes(n.children) : []),
      ]);

    const depthToLevel: Record<number, AllSelectionListsWithSkillTaxonomyKey> =
      {
        0: 'region',
        1: 'country',
      };

    return collectAllNodes(nested).map((node) =>
      treeSelectionToEntitySearchOption({
        id: node.id,
        name: node.name,
        levelId: (depthToLevel[node.depth] ??
          'msa') as AllSelectionListsWithSkillTaxonomyKey,
      })
    );
  }, [geographyLists]);

  // Build maps for city nested ID construction
  const cityIdMaps = useMemo(() => {
    if (!geographyLists) {
      return { msaToCountry: {}, countryToRegion: {} };
    }

    // Build msa -> country map
    const msaToCountry: Record<string, string> = {};
    const msaList = geographyLists.msa ?? [];
    msaList.forEach((msa) => {
      const countryId = Array.isArray(msa.country)
        ? msa.country[0]
        : msa.country;
      if (countryId && msa.id) {
        msaToCountry[msa.id] = countryId;
      }
    });

    // Build country -> region map
    const countryToRegion: Record<string, string> = {};
    const countryList = geographyLists.country ?? [];
    countryList.forEach((country) => {
      const regionId = Array.isArray(country.region)
        ? country.region[0]
        : country.region;
      if (regionId && country.id) {
        countryToRegion[country.id] = regionId;
      }
    });

    return { msaToCountry, countryToRegion };
  }, [geographyLists]);

  // Build city options with nested IDs when searching
  const cityOptions = useMemo(() => {
    if (
      searchTerm.trim().length < MIN_SEARCH_LENGTH ||
      !cityLists?.city ||
      !geographyLists
    ) {
      return [];
    }

    const lower = searchTerm.toLowerCase();
    const { msaToCountry, countryToRegion } = cityIdMaps;

    return (cityLists.city ?? [])
      .filter((city) => {
        const name = city.longName || city.shortName || '';
        return name.toLowerCase().includes(lower);
      })
      .map((city) => {
        // Build nested ID: region_country_msa_city
        const metroArea = Array.isArray(city.metro_area)
          ? city.metro_area[0]
          : city.metro_area;
        const countryId = metroArea ? msaToCountry[metroArea] : undefined;
        const regionId = countryId ? countryToRegion[countryId] : undefined;

        const idParts: string[] = [];
        if (regionId) idParts.push(regionId);
        if (countryId) idParts.push(countryId);
        if (metroArea) idParts.push(metroArea);
        if (city.id) idParts.push(city.id);

        const nestedId = idParts.length > 1 ? idParts.join('_') : city.id || '';

        return treeSelectionToEntitySearchOption({
          id: nestedId,
          name: city.longName || city.shortName || '',
          levelId: 'city' as AllSelectionListsWithSkillTaxonomyKey,
        });
      })
      .slice(0, 10);
  }, [searchTerm, cityLists, geographyLists, cityIdMaps]);

  const filteredOptions = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      return showSuggestionsWhenEmpty ? allOptions.slice(0, 50) : [];
    }

    const lower = searchTerm.toLowerCase();
    const baseFiltered = allOptions
      .filter((opt) => opt.label.toLowerCase().includes(lower))
      .slice(0, 10);

    // Merge city options with base filtered options
    return [...baseFiltered, ...cityOptions];
  }, [searchTerm, allOptions, cityOptions, showSuggestionsWhenEmpty]);

  const options = useMemo(
    () => ensureSelectionsPresent(filteredOptions, storedSelections),
    [filteredOptions, storedSelections]
  );

  const handleInputChange = useCallback(
    (val: string) => setSearchTerm(val),
    []
  );

  return {
    options,
    isLoading: false,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: filteredOptions.length,
  };
};

type RoleConfigOptions = {
  showSuggestionsWhenEmpty?: boolean;
};

export const useRoleEntitySearchConfig = (
  storedSelections: TreeSelection[],
  roleTaxonomySelection: CustomRoleTaxonomySelection,
  { showSuggestionsWhenEmpty = true }: RoleConfigOptions = {}
): EntitySearchSelectConfig => {
  const roleSelectionLists = useRoleSelectionList(roleTaxonomySelection);
  const [searchTerm, setSearchTerm] = useState('');

  const allOptions = useMemo(() => {
    const roles = roleSelectionLists?.role_k150 ?? [];
    if (!Array.isArray(roles)) return [] as EntitySearchOption[];

    return roles
      .map((role) =>
        treeSelectionToEntitySearchOption({
          id: role.id ?? '',
          name: role.longName || role.shortName || '',
          levelId: 'role_k150',
        })
      )
      .filter((option) => option.value.length > 0);
  }, [roleSelectionLists]);

  const filteredOptions = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      return showSuggestionsWhenEmpty ? allOptions.slice(0, 50) : [];
    }

    const lower = searchTerm.toLowerCase();
    return allOptions
      .filter((opt) => opt.label.toLowerCase().includes(lower))
      .slice(0, 25);
  }, [searchTerm, allOptions, showSuggestionsWhenEmpty]);

  const options = useMemo(
    () => ensureSelectionsPresent(filteredOptions, storedSelections),
    [filteredOptions, storedSelections]
  );

  const handleInputChange = useCallback(
    (val: string) => setSearchTerm(val),
    []
  );

  return {
    options,
    isLoading: !roleSelectionLists,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: filteredOptions.length,
  };
};
